import type { FC, ReactNode } from "react";

import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import type { CheckoutConfigurationTaxInfo } from "@jti/commerce";
import { Separator } from "@jti/core-components/components/Separator";
import { type Currency } from "@jti/store/types";
import { FormattedPrice, Txt, useTranslations } from "@jti/ui";

import { PriceBlockTax } from "../PriceBlockTax/PriceBlockTax";

import styles from "../Prices.module.scss";

const { sanitize } = dompurify;

interface PriceBlockProps {
  value: number;
  totalTitle?: boolean;
  currency?: string | Currency;
  shopCurrency: Currency;
  testId?: string;
  title?: string;
  subtitle?: string;
  tax?: any;
  taxInfo?: CheckoutConfigurationTaxInfo;
  topSeparator?: boolean;
  isSummary?: boolean;
  isDiscount?: boolean;
  inProgress?: boolean;
  showNegativePrice?: boolean;
  isShortSummary?: boolean;
  isOriginalPrice?: boolean;
  originalPrice?: ReactNode;
}

export const PriceBlock: FC<PriceBlockProps> = ({
  value,
  totalTitle = false,
  currency,
  shopCurrency,
  testId,
  title = "",
  subtitle = "",
  tax,
  taxInfo,
  topSeparator = false,
  isSummary = false,
  isDiscount = false,
  inProgress = false,
  isShortSummary = false,
  isOriginalPrice = false,
  originalPrice,
}) => {
  const containerClassName = classNames(styles.columns, {
    [styles.total]: totalTitle,
    [styles.totalNoPadding]: isShortSummary,
  });
  const titleClassName = classNames({
    [styles.totalTitle]: totalTitle,
    [styles.subtotalTitle]: !totalTitle,
    [styles.titleShortSummary]: isShortSummary,
  });
  const valueClassName = classNames({
    [styles.sectionTotalValue]: totalTitle,
    [styles.subtotal]: !totalTitle,
    [styles.totalValue]: totalTitle,
    [styles.totalValueBold]: isSummary,
    [styles.totalTitleShort]: isShortSummary,
    [styles.originalPrice]: isOriginalPrice,
    [styles.valueShortSummary]: isShortSummary && !totalTitle,
  });
  const valueContainerClassNames = classNames(styles.valueContainer, { [styles.inProgress]: inProgress });
  const { __ } = useTranslations();

  if (!value && value !== 0) {
    return null;
  }

  const SubtitleRow = () => (subtitle ? <div className={styles.subtitle}>{subtitle}</div> : null);

  const currencyCode = typeof currency === "object" ? currency.code : currency;
  const formattedValue = isDiscount && value > 0 ? -value : value;

  const isPriceUnavailable = value === -1 && !isDiscount;
  const price = isPriceUnavailable ? (
    `${__("cart.prices.na")} `
  ) : (
    <FormattedPrice value={formattedValue} currency={currencyCode || shopCurrency.code} />
  );

  const getTaxesClassName = (withTaxInfo?: boolean) =>
    classNames(styles.taxes, {
      [styles.taxesInfo]: withTaxInfo,
      [styles.taxesInfoShortSummary]: isShortSummary,
    });

  return (
    <>
      {topSeparator ? (
        <div className={classNames(styles.separator, { [styles.shortSummarySeparator]: isShortSummary })}>
          <Separator />
        </div>
      ) : null}
      <div className={containerClassName}>
        <div>
          <div className={titleClassName}>{title}</div>
          <SubtitleRow />
        </div>
        <div className={valueContainerClassNames}>
          <Txt className={valueClassName} dataTestId={testId}>
            {price}
          </Txt>
          {originalPrice}
          <PriceBlockTax className={getTaxesClassName()} tax={tax} currencyCode={shopCurrency.code} />
        </div>

        {taxInfo?.visible && !!taxInfo?.message.length && (
          <div className={getTaxesClassName(true)} dangerouslySetInnerHTML={{ __html: sanitize(taxInfo.message) }} />
        )}
      </div>
    </>
  );
};
